import React, { useEffect, useState } from "react";
import TrustdService from "../service/TrustdService";
import { Button, Card, Modal, Spinner } from "../components/UI";
import ImageInspectorModal from "../components/ImageInspectorModal";

function StatusTag({ status }) {
  const value = String(status || "");
  return <span className={`status-tag status-${value.toLowerCase()}`}>{value.replace(/_/g, " ")}</span>;
}

const asObject = (value) => (value && typeof value === "object" && !Array.isArray(value) ? value : {});

const humanizeReason = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

function IdVerificationDetails({ request, onClose }) {
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const result = asObject(request?.IdVerificationResult);
  const document = asObject(result.document);
  const checks = asObject(result.checks);
  const extracted = asObject(result.extracted);
  const storage = asObject(document.storage);
  const signedUrl = document.signedUrl || "";
  const documentDeletedAt = document.deletedAt || result.documentDeletedAt || "";
  const failures = Array.isArray(result.failureReasons) ? result.failureReasons : [];
  const hasDetails = Object.keys(result).length > 0;

  const rows = [
    ["First Name", request?.GuestInfo?.first_name, extracted.firstName, checks.firstName],
    ["Last Name", request?.GuestInfo?.last_name, extracted.lastName, checks.lastName],
    ["Date of Birth", request?.GuestInfo?.dob, extracted.birthDate, checks.birthDate],
  ];

  useEffect(() => {
    setInspectorOpen(false);
  }, [request?.Id]);

  return (
    <Modal visible={Boolean(request)} title="ID Verification Details" icon="pi-id-card" onClose={onClose} width="min(94vw, 980px)">
      {!hasDetails ? (
        <div className="empty-state">No ID verification details recorded yet.</div>
      ) : (
        <div className="id-verification-details">
          <div className="id-verification-preview">
            {documentDeletedAt ? (
              <div className="guest-id-placeholder id-document-deleted-notice" role="status">
                <i className="pi pi-shield" aria-hidden="true" />
                <strong>ID photo deleted</strong>
                <span>
                  For security, this guest ID photo was permanently deleted
                  after the checkout date passed.
                </span>
              </div>
            ) : signedUrl ? (
              <img
                alt="Guest ID"
                src={signedUrl}
                className="guest-id-secure-image"
                draggable={false}
                referrerPolicy="no-referrer"
                onContextMenu={(event) => event.preventDefault()}
                onClick={() => setInspectorOpen(true)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    setInspectorOpen(true);
                  }
                }}
              />
            ) : (
              <div className="guest-id-placeholder">Signed ID preview is unavailable.</div>
            )}
            {!documentDeletedAt ? (
              <div className="id-storage-note">
                <strong>Encrypted S3 storage</strong>
                <span>{storage.encryption || "Encrypted at rest"}</span>
                {document.signedUrlExpiresAt ? <span>Signed URL expires at {new Date(document.signedUrlExpiresAt).toLocaleString()}</span> : null}
              </div>
            ) : null}
          </div>
          <div className="id-verification-summary">
            <div className="id-status-row">
              <span>Status</span>
              <StatusTag status={request?.IdVerificationStatus || result.status || "required"} />
            </div>
            <div className="id-check-table">
              {rows.map(([label, submitted, parsed, passed]) => (
                <div className="id-check-table-row" key={label}>
                  <span>{label}</span>
                  <strong>{submitted || "N/A"}</strong>
                  <em>{parsed || "Not read"}</em>
                  <StatusTag status={passed ? "PASS" : "FAIL"} />
                </div>
              ))}
            </div>
            <div className="id-failure-list">
              <strong>Mismatches</strong>
              {failures.length > 0 ? (
                failures.map((failure) => <span key={failure}>{humanizeReason(failure)}</span>)
              ) : (
                <span>No mismatch recorded.</span>
              )}
            </div>
          </div>
        </div>
      )}
      <ImageInspectorModal
        visible={inspectorOpen && Boolean(signedUrl) && !Boolean(documentDeletedAt)}
        src={signedUrl}
        alt="Guest ID"
        title="Guest ID Fullscreen Inspector"
        onClose={() => setInspectorOpen(false)}
      />
    </Modal>
  );
}

export default function PastGuestFormRequests() {
  const [guestFormRequests, setGuestFormRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailsRequest, setDetailsRequest] = useState(null);

  const refresh = () => {
    setLoading(true);
    TrustdService.listGuestFormRequestByHostEmail()
      .then((data) => {
        setGuestFormRequests((data || []).reverse());
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    refresh();
  }, []);

  if (loading && guestFormRequests.length === 0) return <Spinner />;

  return (
    <div className="guest-requests-page guest-requests-modern">
      <Card className="guest-requests-card">
        <div className="guest-requests-head">
          <div>
            <p className="dashboard-section-kicker">Guest Forms</p>
            <h1>Past Guest Form Requests</h1>
            <p>
              Review sent forms, completion status, and guest ID verification
              details.
            </p>
          </div>
          <Button
            className="btn-secondary"
            label={loading ? "Refreshing..." : "Refresh"}
            onClick={refresh}
            disabled={loading}
          />
        </div>

        <div className="guest-requests-summary">
          <p>
            <strong>{guestFormRequests.length}</strong> requests
          </p>
          {loading ? <span>Updating requests...</span> : null}
        </div>

        <div className="table-scroll guest-requests-table-wrap">
          <table className="data-table guest-requests-table">
            <thead>
              <tr>
                <th>First Name</th>
                <th>Middle Name</th>
                <th>Last Name</th>
                <th>Date of Birth</th>
                <th>Check-in</th>
                <th>Check-out</th>
                <th>Listing</th>
                <th>Listing Channel</th>
                <th>Booking Id</th>
                <th>Status</th>
                <th>ID Verification</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {guestFormRequests.length === 0 ? (
                <tr>
                  <td colSpan="12" className="guest-requests-empty">
                    No guest form requests yet.
                  </td>
                </tr>
              ) : (
                guestFormRequests.map((request) => (
                  <tr key={request.Id}>
                    <td>{request.GuestInfo?.first_name || "-"}</td>
                    <td>{request.GuestInfo?.middle_name || "-"}</td>
                    <td>{request.GuestInfo?.last_name || "-"}</td>
                    <td>{request.GuestInfo?.dob || "-"}</td>
                    <td>{request.BookingInfo?.check_in_date || "-"}</td>
                    <td>{request.BookingInfo?.check_out_date || "-"}</td>
                    <td title={request.BookingInfo?.listing_name}>
                      {request.BookingInfo?.listing_name || "-"}
                    </td>
                    <td>{request.BookingInfo?.listing_channel || "-"}</td>
                    <td>{request.BookingInfo?.booking_id || "-"}</td>
                    <td>
                      <StatusTag status={request.Status} />
                    </td>
                    <td>
                      <StatusTag
                        status={
                          request.IdVerificationStatus ||
                          (request.IdVerificationRequired
                            ? "required"
                            : "not_required")
                        }
                      />
                    </td>
                    <td>
                      {request.IdVerificationRequired ? (
                        <Button
                          className="btn-sm"
                          label="Details"
                          onClick={() => setDetailsRequest(request)}
                        />
                      ) : (
                        "N/A"
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
      <IdVerificationDetails request={detailsRequest} onClose={() => setDetailsRequest(null)} />
    </div>
  );
}
